/**
 * PATH: apps/quote/src/pages/claims/claims-list-page/ClaimRow.tsx
 * FILE_ID: QUOTE-CLAIMS-LIST-ROW-001
 * PURPOSE: Individual claim table row with actions menu
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Building2, Calendar, MoreHorizontal, Eye, Edit,
  Send, CheckCircle, DollarSign, Trash2,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import type { PaymentClaim } from './types'
import { STATUS_CONFIG, isClaimEditable } from './types'

function StatusBadge({ status }: { status: PaymentClaim['status'] }) {
  const config = STATUS_CONFIG[status]
  const Icon = config.icon
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium', config.color)}>
      <Icon className="w-3.5 h-3.5" />{config.label}
    </span>
  )
}

function ProgressBar({ percent }: { percent: number }) {
  const p = Math.min(100, Math.max(0, percent))
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
        <div className={cn('h-full rounded-full transition-all duration-300', p >= 100 ? 'bg-emerald-500' : p >= 75 ? 'bg-green-500' : p >= 50 ? 'bg-blue-500' : p >= 25 ? 'bg-amber-500' : 'bg-slate-400')} style={{ width: `${p}%` }} />
      </div>
      <span className="text-xs font-medium text-slate-600 dark:text-slate-400 w-12 text-right">{p.toFixed(0)}%</span>
    </div>
  )
}

interface ClaimRowProps {
  claim: PaymentClaim
  onAction: (action: string, claim: PaymentClaim) => void
}

export function ClaimRow({ claim, onAction }: ClaimRowProps) {
  const [showActions, setShowActions] = useState(false)
  const sequence = (claim as PaymentClaim & {
    contract_sequence?: number | null
  }).contract_sequence ?? claim.claim_number
  const canSendXero = ((claim as { available_actions?: string[] }).available_actions ?? [])
    .includes('send_to_xero')

  return (
    <tr className="group hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
      <td className="px-4 py-4">
        <div className="flex flex-col">
          <Link to={`/claims/${claim.id}`} className="font-semibold text-slate-900 dark:text-white hover:text-amber-600 dark:hover:text-amber-400 transition-colors">
            {/* One document spine: doc_type distinguishes claims from direct
                invoices; external rows are provenance for docs sent outside. */}
            {(claim as { doc_type?: string }).doc_type === 'invoice' ? 'Invoice' : 'Claim'} #{sequence}
            {(claim as { source?: string }).source === 'external' && (
              <span className="ml-2 text-[10px] font-medium uppercase tracking-wide text-slate-400">external</span>
            )}
          </Link>
          <div className="flex items-center gap-2 mt-1">
            <Building2 className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-sm text-slate-600 dark:text-slate-400">{claim.job.job_number} - {claim.job.name}</span>
          </div>
          <span className="text-xs text-slate-500 dark:text-slate-500 mt-0.5">{claim.job.client_name}</span>
        </div>
      </td>
      <td className="px-4 py-4">
        <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400" /><span className="text-sm text-slate-700 dark:text-slate-300">{claim.period}</span></div>
      </td>
      <td className="px-4 py-4"><StatusBadge status={claim.status} /></td>
      <td className="px-4 py-4 text-right">
        <div className="flex flex-col items-end">
          <span className="font-semibold text-slate-900 dark:text-white">{formatCurrency(parseFloat(claim.this_claim_incl_gst))}</span>
          <span className="text-xs text-slate-500 dark:text-slate-400">ex GST: {formatCurrency(parseFloat(claim.this_claim))}</span>
        </div>
      </td>
      <td className="px-4 py-4"><div className="w-32"><ProgressBar percent={claim.progress_percent} /></div></td>
      <td className="px-4 py-4 text-right">
        <div className="flex flex-col items-end">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{formatCurrency(parseFloat(claim.total_claimed))}</span>
          <span className="text-xs text-slate-500 dark:text-slate-400">of {formatCurrency(parseFloat(claim.revised_contract_value))}</span>
        </div>
      </td>
      <td className="px-4 py-4">
        <div className="relative">
          <button onClick={() => setShowActions(!showActions)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors opacity-0 group-hover:opacity-100">
            <MoreHorizontal className="w-5 h-5 text-slate-500" />
          </button>
          {showActions && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowActions(false)} />
              <div className="absolute right-0 top-full mt-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 min-w-[160px] z-20">
                <button onClick={() => { onAction('view', claim); setShowActions(false) }} className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"><Eye className="w-4 h-4" />View Details</button>
                {isClaimEditable(claim) && <button onClick={() => { onAction('edit', claim); setShowActions(false) }} className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"><Edit className="w-4 h-4" />Edit Claim</button>}
                {claim.status === 'draft' && <button onClick={() => { onAction('submit', claim); setShowActions(false) }} className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-blue-600 dark:text-blue-400"><Send className="w-4 h-4" />Submit for Approval</button>}
                {['submitted', 'under_review'].includes(claim.status) && <button onClick={() => { onAction('approve', claim); setShowActions(false) }} className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-green-600 dark:text-green-400"><CheckCircle className="w-4 h-4" />Approve</button>}
                {claim.status === 'approved' && canSendXero && <button onClick={() => { onAction('send_xero', claim); setShowActions(false) }} className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-amber-600 dark:text-amber-400"><DollarSign className="w-4 h-4" />Send to Xero</button>}
                {isClaimEditable(claim) && (<><div className="border-t border-slate-200 dark:border-slate-700 my-1" /><button onClick={() => { onAction('delete', claim); setShowActions(false) }} className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 text-red-600 dark:text-red-400"><Trash2 className="w-4 h-4" />Delete</button></>)}
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  )
}
